import { useCallback, useMemo, useState } from 'react';
import type { WorkflowDefinition, WorkflowStepCatalog } from './utils/workflow-definition';
import {
  focusedStepIds,
  foldableOutputs,
  hiddenStepCounts,
  revealStep,
  visibleStepIds,
} from './utils/workflow-folding';
import { findSearchMatches, type WorkflowSearch } from './utils/workflow-search';

/** A request for the canvas to select a step and bring it into view; the counter makes a repeat count. */
export interface WorkflowNavTarget {
  nodeId: string;
  seq: number;
}

interface CanvasViewState {
  /** The version these folds and this search belong to; another version starts over. */
  versionId: string | undefined;
  folded: ReadonlySet<string>;
  search: WorkflowSearch;
  /** Index into the matches of the one the canvas is on. */
  matchIndex: number;
  /** The step to select and centre next; the canvas clears it once it has. */
  navTarget: WorkflowNavTarget | null;
  /** The focused step, while the canvas lights its path and the drawer sums it up. */
  focusedId: string | null;
}

const EMPTY_SEARCH: WorkflowSearch = { query: '', stepType: '' };

const initialState = (versionId: string | undefined): CanvasViewState => ({
  versionId,
  folded: new Set(),
  search: EMPTY_SEARCH,
  matchIndex: 0,
  navTarget: null,
  focusedId: null,
});

const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>) =>
  a.size === b.size && [...a].every((key) => b.has(key));

export interface WorkflowCanvasView {
  folded: ReadonlySet<string>;
  /** How many steps each folded output hides; a step two outputs lead to is counted once. */
  hiddenCounts: ReadonlyMap<string, number>;
  toggleFold: (key: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  /**
   * Focuses `nodeId`: the canvas greys out every step off its path, the drawer sums the path up,
   * and the view selects and centres it. A fold hiding it opens.
   */
  focusStep: (nodeId: string) => void;
  focusedId: string | null;
  /** The way from the trigger to the focused step, and the step; empty with no focus. */
  focusedIds: ReadonlySet<string>;
  clearFocus: () => void;
  /** Unfolds whatever hides `nodeId`, then selects and centres it. */
  showStep: (nodeId: string) => void;
  /** Unfolds whatever hides `nodeId`, without moving the view. */
  revealStep: (nodeId: string) => void;
  /** The step the canvas should select and centre next; it calls `navDone` once it has. */
  navTarget: WorkflowNavTarget | null;
  navDone: (target: WorkflowNavTarget) => void;
  /** The steps not folded away, trigger included. */
  shownStepIds: ReadonlySet<string>;
  search: WorkflowSearch;
  setSearch: (next: WorkflowSearch) => void;
  /** Something is typed or a type is picked; steps that don't match fade. */
  searchActive: boolean;
  matchIds: string[];
  /** The match the canvas is on; `null` with no search or no match. */
  currentMatchId: string | null;
  /** 0-based position of `currentMatchId` among `matchIds`; -1 with none. */
  currentMatchIndex: number;
  /** Moves to the next (1) or previous (-1) match, wrapping round, and shows it. */
  stepMatch: (delta: 1 | -1) => void;
}

/**
 * What the canvas is showing of a version, apart from the steps themselves: folded outputs, the
 * search and the match it's on, the focused step, and the step to bring into view next. Kept per
 * version, and never part of a save.
 */
export function useWorkflowCanvasView({
  versionId,
  definition,
  catalog,
}: {
  versionId: string | undefined;
  definition: WorkflowDefinition | null;
  catalog: WorkflowStepCatalog;
}): WorkflowCanvasView {
  const [state, setState] = useState<CanvasViewState>(() => initialState(versionId));

  // Another version: start over, while rendering, as the draft editor does.
  let current = state;
  if (state.versionId !== versionId) {
    current = initialState(versionId);
    setState(current);
  }
  const { folded, search, matchIndex, navTarget } = current;
  // a focused step that an edit deleted is no longer focused
  const focusedId =
    current.focusedId && definition?.nodes.some(({ id }) => id === current.focusedId)
      ? current.focusedId
      : null;

  /** Sets the folds, keeping the same set when nothing changed so the canvas isn't laid out again. */
  const setFolded = useCallback(
    (next: (previous: ReadonlySet<string>) => ReadonlySet<string>) =>
      setState((previous) => {
        const folds = next(previous.folded);
        return sameSet(folds, previous.folded) ? previous : { ...previous, folded: folds };
      }),
    [],
  );

  const toggleFold = useCallback(
    (key: string) =>
      setFolded((previous) => {
        const next = new Set(previous);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [setFolded],
  );

  const expandAll = useCallback(() => setFolded(() => new Set()), [setFolded]);

  const collapseAll = useCallback(() => {
    if (definition) setFolded(() => foldableOutputs(definition));
  }, [definition, setFolded]);

  /** Unfolds what hides `nodeId` and asks the canvas to select and centre it. */
  const navigate = useCallback(
    (previous: CanvasViewState, nodeId: string): CanvasViewState => ({
      ...previous,
      folded: definition ? revealStep(definition, previous.folded, nodeId) : previous.folded,
      navTarget: { nodeId, seq: (previous.navTarget?.seq ?? 0) + 1 },
    }),
    [definition],
  );

  const focusStep = useCallback(
    (nodeId: string) =>
      setState((previous) => ({ ...navigate(previous, nodeId), focusedId: nodeId })),
    [navigate],
  );

  const showStep = useCallback(
    (nodeId: string) => setState((previous) => navigate(previous, nodeId)),
    [navigate],
  );

  const navDone = useCallback(
    (target: WorkflowNavTarget) =>
      setState((previous) =>
        previous.navTarget?.seq === target.seq ? { ...previous, navTarget: null } : previous,
      ),
    [],
  );

  const revealStepId = useCallback(
    (nodeId: string) => {
      if (definition) setFolded((previous) => revealStep(definition, previous, nodeId));
    },
    [definition, setFolded],
  );

  const clearFocus = useCallback(
    () => setState((previous) => ({ ...previous, focusedId: null })),
    [],
  );
  const focusedIds = useMemo(
    () =>
      definition && focusedId ? focusedStepIds(definition, focusedId) : new Set<string>(),
    [definition, focusedId],
  );

  const shownStepIds = useMemo(
    () => (definition ? visibleStepIds(definition, folded) : new Set<string>()),
    [definition, folded],
  );
  const hiddenCounts = useMemo(
    () => (definition ? hiddenStepCounts(definition, folded) : new Map<string, number>()),
    [definition, folded],
  );

  const matchIds = useMemo(
    () => (definition ? findSearchMatches(definition, catalog, search) : []),
    [catalog, definition, search],
  );
  const searchActive = !!search.query.trim() || !!search.stepType;
  // wraps round both ways: Previous on the first match goes to the last
  const currentMatchIndex = matchIds.length
    ? ((matchIndex % matchIds.length) + matchIds.length) % matchIds.length
    : -1;
  const currentMatchId: string | null = matchIds[currentMatchIndex] ?? null;

  // A new search shows its first match; Next and Previous show theirs. Done here, where the search
  // changes, so editing a step's text while a search is open never moves the view.
  const setSearch = useCallback(
    (next: WorkflowSearch) =>
      setState((previous) => {
        const updated = { ...previous, search: next, matchIndex: 0 };
        const [first] = definition ? findSearchMatches(definition, catalog, next) : [];
        return first ? navigate(updated, first) : updated;
      }),
    [catalog, definition, navigate],
  );

  const stepMatch = useCallback(
    (delta: 1 | -1) =>
      setState((previous) => {
        const matches = definition
          ? findSearchMatches(definition, catalog, previous.search)
          : [];
        if (!matches.length) return previous;
        const index = previous.matchIndex + delta;
        const target = matches[((index % matches.length) + matches.length) % matches.length];
        return navigate({ ...previous, matchIndex: index }, target);
      }),
    [catalog, definition, navigate],
  );

  return useMemo(
    () => ({
      folded,
      hiddenCounts,
      toggleFold,
      expandAll,
      collapseAll,
      focusStep,
      focusedId,
      focusedIds,
      clearFocus,
      showStep,
      revealStep: revealStepId,
      navTarget,
      navDone,
      shownStepIds,
      search,
      setSearch,
      searchActive,
      matchIds,
      currentMatchId,
      currentMatchIndex,
      stepMatch,
    }),
    [
      clearFocus,
      collapseAll,
      currentMatchId,
      currentMatchIndex,
      expandAll,
      focusStep,
      focusedId,
      focusedIds,
      folded,
      hiddenCounts,
      matchIds,
      navDone,
      navTarget,
      revealStepId,
      search,
      searchActive,
      setSearch,
      showStep,
      shownStepIds,
      stepMatch,
      toggleFold,
    ],
  );
}
